import {shape, string} from 'prop-types';
import {modify} from 'partial.lenses';
import {isEmpty, test} from 'ramda';
import {
    validatorShape,
    hasErrors as hasValidationErrors,
} from '@sinclair-digital/sbg-storyline-components/models';
import {pipeAllEndo} from '@sinclair-digital/sbg-util/dist/es/function';
import {PropertySiteV1} from '@sinclair-digital/stl-common/src/models/Property';
import {Validator, toValidatedField} from '@sinclair-digital/stl-common/src/ValidatedField';

export interface Social {
    facebookUrl: Validator<string>;
    twitterUrl: Validator<string>;
    googlePlusUrl: Validator<string>;
    pinterestUrl: Validator<string>;
    instagramFeedUrl: Validator<string>;
    youtubeChannelUrl: Validator<string>;
    facebookAppId: string;
    livestreamFacebookAppId: string;
    twitterAppId: string;
    googlePlusAppId: string;
    fbPages: string;
    youtubeLivestreamId: string;
    youtubeChannelId: string;
}
export const socialShape = {
    facebookUrl: shape(validatorShape(string)),
    twitterUrl: shape(validatorShape(string)),
    googlePlusUrl: shape(validatorShape(string)),
    pinterestUrl: shape(validatorShape(string)),
    instagramFeedUrl: shape(validatorShape(string)),
    youtubeChannelUrl: shape(validatorShape(string)),
    facebookAppId: string,
    livestreamFacebookAppId: string,
    twitterAppId: string,
    googlePlusAppId: string,
    fbPages: string,
    youtubeLivestreamId: string,
    youtubeChannelId: string,
};

export const defaultSocialSettings: Social = {
    facebookUrl: toValidatedField(''),
    twitterUrl: toValidatedField(''),
    googlePlusUrl: toValidatedField(''),
    pinterestUrl: toValidatedField(''),
    instagramFeedUrl: toValidatedField(''),
    youtubeChannelUrl: toValidatedField(''),
    facebookAppId: '',
    livestreamFacebookAppId: '',
    twitterAppId: '',
    googlePlusAppId: '',
    fbPages: '',
    youtubeLivestreamId: '',
    youtubeChannelId: '',
};
export const socialOptics = {
    facebookUrl: 'facebookUrl',
    twitterUrl: 'twitterUrl',
    googlePlusUrl: 'googlePlusUrl',
    pinterestUrl: 'pinterestUrl',
    instagramFeedUrl: 'instagramFeedUrl',
    youtubeChannelUrl: 'youtubeChannelUrl',
};

const requiredFields = [
    socialOptics.facebookUrl,
    socialOptics.twitterUrl,
    socialOptics.googlePlusUrl,
    socialOptics.pinterestUrl,
    socialOptics.instagramFeedUrl,
    socialOptics.youtubeChannelUrl,
];

export const isUrl = test(
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi,
);

export const validatedFields = requiredFields;

export const validateUrl = <T>(lens: unknown) =>
    modify<T>(lens, (validatorObj: Validator<string>) => {
        const isValid = isEmpty(validatorObj.value) ? true : isUrl(validatorObj.value);
        const errorMessage = isValid ? undefined : 'Invalid url';
        return validatorObj.errorMessage === errorMessage
            ? validatorObj
            : {
                ...validatorObj,
                errorMessage,
            };
    });

export const validateUrlFields = (lenses: unknown[]) =>
    pipeAllEndo<Social>(lenses.map(lens => validateUrl<Social>(lens)));

export const validate = validateUrlFields(requiredFields);

export const hasSocialSettingsErrors = hasValidationErrors(validatedFields);

export const parseSocialSettings = ({
    social: {
        facebookUrl = '',
        twitterUrl = '',
        googlePlusUrl = '',
        pinterestUrl = '',
        instagramFeedUrl = '',
        youtubeChannelUrl = '',
        facebookAppId = '',
        livestreamFacebookAppId = '',
        twitterAppId = '',
        googlePlusAppId = '',
        fbPages = '',
        youtubeLivestreamId = '',
        youtubeChannelId = '',
    },
}: PropertySiteV1): Social => {
    return {
        facebookUrl: toValidatedField(facebookUrl),
        twitterUrl: toValidatedField(twitterUrl),
        googlePlusUrl: toValidatedField(googlePlusUrl),
        pinterestUrl: toValidatedField(pinterestUrl),
        instagramFeedUrl: toValidatedField(instagramFeedUrl),
        youtubeChannelUrl: toValidatedField(youtubeChannelUrl),
        facebookAppId,
        livestreamFacebookAppId,
        twitterAppId,
        googlePlusAppId,
        fbPages,
        youtubeLivestreamId,
        youtubeChannelId,
    };
};

export const serializeSocialSettings = ({
    facebookUrl,
    twitterUrl,
    googlePlusUrl,
    pinterestUrl,
    instagramFeedUrl,
    youtubeChannelUrl,
    facebookAppId,
    livestreamFacebookAppId,
    twitterAppId,
    googlePlusAppId,
    fbPages,
    youtubeLivestreamId,
    youtubeChannelId,
}: Social): PropertySiteV1['social'] => ({
    facebookUrl: facebookUrl.value,
    twitterUrl: twitterUrl.value,
    googlePlusUrl: googlePlusUrl.value,
    pinterestUrl: pinterestUrl.value,
    instagramFeedUrl: instagramFeedUrl.value,
    youtubeChannelUrl: youtubeChannelUrl.value,
    facebookAppId,
    livestreamFacebookAppId,
    twitterAppId,
    googlePlusAppId,
    fbPages,
    youtubeLivestreamId,
    youtubeChannelId,
});
